import { z } from "zod";

import { depositReadSchema, depositSchema } from "./deposit.js";

/**
 * `P1-DOS-073` (DEALER V2 B5-a): **`GET /api/v1/me/dealership/dossiers`'s answer, one contract for the door and for every client** (A48: a stub speaks the provider's contract). One row per dossier on the
 * dealership's cars, both channels; the status in plain words, who waits and the next step are SERVED (one server function), the counts of the filter chips too: the browser prints and computes nothing.
 *
 * **One definition, two readings** (`DECISION-ANSWER-LENIENT`'s pattern): the shapes are written once with the object constructor as the parameter; `z.strictObject` is the contract (stubs, the OpenAPI parity
 * spec), `z.looseObject` is the READ twin a client parses the live answer with (extra keys tolerated at every level, every required field and type identical).
 */
function buildSchemas(
  object: typeof z.strictObject,
  asOf: z.ZodType<string | undefined>,
  deposit: typeof depositSchema,
) {
  const waitingOn = z.enum(["dealer", "client", "hakili", "bank"]);

  /**
   * The row's ONE action, where a dealer door exists and would accept the call; `null` otherwise. The target carries every argument the door's route needs. `emettreProforma` is
   * `POST /api/v1/me/vehicles/:vehicleId/dossier-requests/:dossierId/proforma/issue` (no body); `confirmerReception` is `POST /api/v1/me/settlements/:settlementId/confirm-receipt`, which also takes an
   * `Idempotency-Key` header and the day the dealer received the money (`receivedOn`, AAAA-MM-JJ), both supplied by the caller at call time.
   */
  const actionSchema = z.discriminatedUnion("code", [
    object({
      code: z.literal("emettreProforma"),
      label: z.string(),
      target: object({ vehicleId: z.string(), dossierId: z.string() }),
    }),
    object({
      code: z.literal("confirmerReception"),
      label: z.string(),
      target: object({ settlementId: z.string() }),
    }),
  ]);

  const dealerDossierSchema = object({
    id: z.string(),
    reference: z.string(),
    /** `client` for every row until the assisted creation exists (B2); then the dossier's recorded creator. */
    origin: object({ code: z.enum(["client", "concession"]), words: z.string() }),
    /** Served to the dealer because a dossier on his car is a financing request (founder D-1031-4, point 3). */
    client: object({ name: z.string(), phone: z.string() }),
    vehicle: object({ id: z.string(), label: z.string(), stockReference: z.string().nullable() }),
    /** The dossier's stage code (`dossier.dossier_stage`). */
    stage: z.string(),
    status: object({
      whereText: z.string(),
      waitingOn: waitingOn.nullable(),
      waitingText: z.string().nullable(),
      nextText: z.string().nullable(),
      action: actionSchema.nullable(),
    }),
    /**
     * The parcours, served: the steps in order and the one the dossier stands on (`currentStep`, an index into `steps`; `null` before the bank has the file and once it is refused). The words are RULED (A to H architect, 31 October 2026) and served from `dealer-dossier-words.ts`, `PARCOURS_WORDS`.
     */
    parcours: object({ steps: z.array(z.string()), currentStep: z.number().int().nullable() }),
    /** The lead bank file of the dossier (the most advanced live one), `null` while it was not sent to a bank. */
    bank: object({
      name: z.string(),
      stage: z.string(),
      otherBanks: z.number().int(),
      otherBanksText: z.string().nullable(),
    }).nullable(),
    /** `DEPOSIT-D`: the requested deposit from the opening, the offered one once an offer is retained; `null` where the price is unknown. */
    deposit: deposit.nullable(),
    /** ISO instant the dossier entered its stage. */
    since: z.string(),
  });

  const dealerDossiersAnswerSchema = object({
    /** The SERVER's instant, once per answer, ISO 8601, the same name and type as the dealer inventory's `asOf`: « depuis N jours » and a default day are derived from it, never from the browser's clock (OD-17). Optional on the READ twin only, so a client reading an older answer does not break. */
    asOf,
    dossiers: z.array(dealerDossierSchema),
    counts: object({
      all: z.number().int(),
      byWaitingOn: object({
        dealer: z.number().int(),
        client: z.number().int(),
        hakili: z.number().int(),
        bank: z.number().int(),
        nobody: z.number().int(),
      }),
      byOrigin: object({ client: z.number().int(), concession: z.number().int() }),
    }),
  });

  return { dealerDossierSchema, dealerDossiersAnswerSchema };
}

const strict = buildSchemas(z.strictObject, z.string(), depositSchema);
const loose = buildSchemas(z.looseObject, z.string().optional(), depositReadSchema);

export const dealerDossierSchema = strict.dealerDossierSchema;
/** The READ twin of one row (extra keys tolerated), for the detail answer's lenient reading. */
export const dealerDossierReadSchema = loose.dealerDossierSchema;
export const dealerDossiersAnswerSchema = strict.dealerDossiersAnswerSchema;

/** The READ twin: what a client parses the live answer with; extra keys pass at every level, a missing required field or a wrong type fails as in the strict schema. */
export const dealerDossiersAnswerReadSchema = loose.dealerDossiersAnswerSchema;

export type DealerDossiersAnswer = z.infer<typeof dealerDossiersAnswerSchema>;
export type DealerDossier = z.infer<typeof dealerDossierSchema>;
